/**
 * SearchInput Component
 * Debounced search input with clear button
 * Prevents excessive API calls on keystroke
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/**
 * SearchInput Component
 *
 * @param {string} value - Controlled value
 * @param {function} onChange - Change handler (debounced)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} allowClear - Show clear button (default: true)
 * @param {Object} style - Additional styles
 * @param {Object} rest - Additional Input props
 */
function SearchInput({
  value: propValue,
  onChange,
  debounceMs = 300,
  placeholder = 'Search...',
  allowClear = true,
  style = {},
  ...rest
}) {
  const [inputValue, setInputValue] = useState(propValue || '');
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  // Sync with prop value changes
  useEffect(() => {
    setInputValue(propValue || '');
  }, [propValue]);

  // Debounced onChange
  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      // Clear existing timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        if (onChange) {
          onChange(newValue);
        }
      }, debounceMs);

      setDebounceTimeout(timeout);
    },
    [onChange, debounceMs, debounceTimeout]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  return (
    <Input
      prefix={<SearchOutlined />}
      placeholder={placeholder}
      value={inputValue}
      onChange={handleChange}
      allowClear={allowClear}
      style={{ width: 250, ...style }}
      {...rest}
    />
  );
}

export default SearchInput;
